import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { ChangeSetFileContent, GitIdentity } from "@racler/protocol";
import type { GitSetupApi } from "../lib/gitSetup.ts";
import { Dialog } from "./Dialog.tsx";
import { ReviewDiff } from "./ReviewDiff.tsx";
import { ReviewFileList } from "./ReviewFileList.tsx";

export interface WorkingTreePreflightProps {
  kind: "unsaved" | "dirty";
  changedCount?: number;
  untrackedCount?: number;
  modifiedCount?: number;
  gitApi?: GitSetupApi | null;
  hasUnsavedBuffers?: () => boolean;
  onSaveAllAndContinue: () => Promise<void>;
  onCommitAndStart: (message: string, identity?: GitIdentity) => Promise<boolean>;
  onDiscardAndStart?: () => Promise<boolean>;
  onClose: () => void;
  initialReviewing?: boolean;
  initialConfirmingDiscard?: boolean;
}

export function WorkingTreePreflightDialog({
  kind,
  changedCount = 1,
  untrackedCount,
  modifiedCount,
  gitApi,
  hasUnsavedBuffers,
  onSaveAllAndContinue,
  onCommitAndStart,
  onDiscardAndStart,
  onClose,
  initialReviewing = false,
  initialConfirmingDiscard = false,
}: WorkingTreePreflightProps) {
  const [commitMessage, setCommitMessage] = useState("Changes before Autonomous run");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsIdentity, setNeedsIdentity] = useState(false);
  const [identity, setIdentity] = useState<GitIdentity>({ name: "", email: "" });
  const [reviewing, setReviewing] = useState(initialReviewing);
  const [diffFiles, setDiffFiles] = useState<ChangeSetFileContent[] | null>(null);
  const [loadingDiff, setLoadingDiff] = useState(false);
  const [selectedFileIndex, setSelectedFileIndex] = useState(0);
  const [confirmingDiscard, setConfirmingDiscard] = useState(initialConfirmingDiscard);

  const identityValid = identity.name.trim() !== "" && identity.email.includes("@");

  const handleSaveAll = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSaveAllAndContinue();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleCommit = async () => {
    setBusy(true);
    setError(null);
    try {
      const ok = await onCommitAndStart(
        commitMessage,
        needsIdentity ? identity : undefined,
      );
      if (!ok) {
        if (!identityValid && needsIdentity) {
          setError("Please enter a valid name and email for this commit.");
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (
        msg.toLowerCase().includes("identity") ||
        msg.toLowerCase().includes("user.name") ||
        msg.toLowerCase().includes("user.email")
      ) {
        setNeedsIdentity(true);
        if (reviewing) setReviewing(false);
      }
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleOpenReview = async () => {
    setReviewing(true);
    setError(null);
    if (!diffFiles && gitApi?.diff) {
      setLoadingDiff(true);
      try {
        const res = await gitApi.diff();
        setDiffFiles(res.files);
        setSelectedFileIndex(0);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoadingDiff(false);
      }
    }
  };

  const handleOpenDiscardConfirm = () => {
    setError(null);
    if (hasUnsavedBuffers?.()) {
      setError("You have unsaved files. Save or close them before discarding local changes.");
      return;
    }
    setConfirmingDiscard(true);
  };

  const handleConfirmDiscard = async () => {
    if (hasUnsavedBuffers?.()) {
      setError("You have unsaved files. Save or close them before discarding local changes.");
      setConfirmingDiscard(false);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (!onDiscardAndStart) {
        throw new Error("Discard action is not available.");
      }
      const ok = await onDiscardAndStart();
      if (ok) {
        setConfirmingDiscard(false);
        setReviewing(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  // Keyboard Escape in review modal closes review
  useEffect(() => {
    if (!reviewing || confirmingDiscard) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setReviewing(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [reviewing, confirmingDiscard]);

  const effectiveUntracked = diffFiles
    ? diffFiles.filter((f) => f.op === "create").length
    : untrackedCount ?? 0;
  const effectiveModified = diffFiles
    ? diffFiles.filter((f) => f.op === "modify" || f.op === "delete").length
    : modifiedCount ?? (changedCount - effectiveUntracked > 0 ? changedCount - effectiveUntracked : changedCount);

  const renderDiscardConfirmationBody = () => {
    const parts: string[] = [];
    if (effectiveModified > 0) {
      parts.push(
        `This will permanently discard ${effectiveModified} modified ${
          effectiveModified === 1 ? "file" : "files"
        }.`,
      );
    }
    if (effectiveUntracked > 0) {
      if (effectiveModified > 0) {
        parts.push(
          `${effectiveUntracked} untracked ${effectiveUntracked === 1 ? "file" : "files"} will also be deleted.`,
        );
      } else {
        parts.push(
          `${effectiveUntracked} untracked ${effectiveUntracked === 1 ? "file" : "files"} will be deleted.`,
        );
      }
    }
    if (parts.length === 0) {
      parts.push("This will permanently discard all local changes.");
    }
    parts.push("After the Project is clean, Agent Autonomous will start automatically.");
    return parts.join(" ");
  };

  if (confirmingDiscard) {
    return (
      <Dialog
        title="Discard local changes and start Autonomous?"
        onClose={() => setConfirmingDiscard(false)}
      >
        <div className="dialog-body" data-testid="preflight-discard-confirm-dialog">
          <p data-testid="preflight-discard-confirm-body">
            {renderDiscardConfirmationBody()}
          </p>
          {error && (
            <div className="msg-error" role="alert" data-testid="preflight-error">
              {error}
            </div>
          )}
        </div>
        <div className="row end">
          <button
            type="button"
            onClick={() => setConfirmingDiscard(false)}
            disabled={busy}
            data-testid="preflight-discard-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className="danger"
            onClick={() => void handleConfirmDiscard()}
            disabled={busy}
            data-testid="preflight-confirm-discard-start"
          >
            Discard & Start
          </button>
        </div>
      </Dialog>
    );
  }

  if (reviewing) {
    const summary = (diffFiles ?? []).map((f) => ({
      path: f.path,
      op: f.op,
      baseHash: null,
      additions: 0,
      deletions: 0,
      oldPath: f.oldPath,
    }));
    const currentFile = diffFiles ? diffFiles[selectedFileIndex] : undefined;

    const modal = (
      <div
        className="review-modal-backdrop"
        data-testid="preflight-review-modal"
        onMouseDown={(e) => e.target === e.currentTarget && setReviewing(false)}
      >
        <div className="review-modal" role="dialog" aria-modal="true" aria-label="Review changes">
          <div className="review" data-testid="working-tree-review">
            <div className="review-head">
              <span className="review-title">Review changes</span>
              <span className="muted small" data-testid="review-status">
                {diffFiles ? `${diffFiles.length} ${diffFiles.length === 1 ? "file" : "files"}` : "Loading diff…"}
              </span>
              <span className="review-actions">
                <button
                  type="button"
                  onClick={() => setReviewing(false)}
                  disabled={busy}
                  data-testid="preflight-review-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="danger"
                  disabled={busy}
                  onClick={handleOpenDiscardConfirm}
                  data-testid="preflight-review-discard-start"
                >
                  Discard & Start
                </button>
                <button
                  type="button"
                  className="primary"
                  disabled={busy || (needsIdentity && !identityValid)}
                  onClick={() => void handleCommit()}
                  data-testid="preflight-review-commit-start"
                >
                  Commit & Start Autonomous
                </button>
              </span>
            </div>
            {error && (
              <div className="msg-error" role="alert" data-testid="preflight-error">
                {error}
              </div>
            )}
            <div className="review-body">
              <ReviewFileList
                files={diffFiles}
                summary={summary}
                selected={selectedFileIndex}
                loading={loadingDiff}
                onSelect={setSelectedFileIndex}
              />
              <ReviewDiff file={currentFile} />
            </div>
          </div>
        </div>
      </div>
    );

    return typeof document !== "undefined" ? createPortal(modal, document.body) : modal;
  }

  if (kind === "unsaved") {
    return (
      <Dialog title="Save changes before starting Autonomous" onClose={onClose}>
        <div className="dialog-body" data-testid="preflight-unsaved-dialog">
          <p>
            You have unsaved files. Save them before creating the clean Git baseline required for Agent
            Autonomous.
          </p>
          {error && (
            <div className="msg-error" role="alert" data-testid="preflight-error">
              {error}
            </div>
          )}
        </div>
        <div className="row end">
          <button type="button" onClick={onClose} disabled={busy} data-testid="preflight-save-cancel">
            Cancel
          </button>
          <button
            type="button"
            className="primary"
            onClick={() => void handleSaveAll()}
            disabled={busy}
            data-testid="preflight-save-all"
          >
            Save All & Continue
          </button>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog title="Prepare project for Autonomous" onClose={onClose}>
      <div className="dialog-body" data-testid="preflight-dirty-dialog">
        <p data-testid="preflight-dirty-body">
          Agent Autonomous needs a clean project baseline.{" "}
          {changedCount === 1 ? "1 local file has" : `${changedCount} local files have`} uncommitted changes.
        </p>
        <div style={{ marginBottom: "10px" }}>
          <label className="muted small" style={{ display: "block", marginBottom: "4px" }}>
            Commit message
          </label>
          <input
            type="text"
            value={commitMessage}
            onChange={(e) => setCommitMessage(e.target.value)}
            placeholder="Changes before Autonomous run"
            data-testid="preflight-commit-message"
          />
        </div>
        {needsIdentity && (
          <div data-testid="git-identity" style={{ marginBottom: "10px" }}>
            <p className="muted small">
              Git has no name/email for commits. They are used for this one commit only and are not saved to any
              Git settings.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <input
                placeholder="Name"
                value={identity.name}
                onChange={(e) => setIdentity({ ...identity, name: e.target.value })}
                data-testid="git-identity-name"
              />
              <input
                placeholder="Email"
                value={identity.email}
                onChange={(e) => setIdentity({ ...identity, email: e.target.value })}
                data-testid="git-identity-email"
              />
            </div>
          </div>
        )}
        {error && (
          <div className="msg-error" role="alert" data-testid="preflight-error">
            {error}
          </div>
        )}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button
          type="button"
          onClick={() => void handleOpenReview()}
          disabled={busy}
          data-testid="preflight-review-changes"
        >
          Review changes
        </button>
        <div className="row">
          <button type="button" onClick={onClose} disabled={busy} data-testid="preflight-commit-cancel">
            Cancel
          </button>
          <button
            type="button"
            className="danger"
            onClick={handleOpenDiscardConfirm}
            disabled={busy}
            data-testid="preflight-discard-start"
          >
            Discard & Start
          </button>
          <button
            type="button"
            className="primary"
            onClick={() => void handleCommit()}
            disabled={busy || (needsIdentity && !identityValid)}
            data-testid="preflight-commit-start"
          >
            Commit & Start
          </button>
        </div>
      </div>
    </Dialog>
  );
}
